import React from 'react';
import { ApolloError } from '@apollo/client';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Pagination,
  IPaginationProps,
  LinearProgress,
  LoadedContent,
} from '@occc/ui-design-system';
import { CampaignTableRow } from '@occc-app/routes/Campaign/CampaignListPage/components/CampaignsTableRow';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';
import { CampaignWithCampaignComponentsSanitized } from '../types';

type CampaignsTableProps = {
  campaigns?: CampaignWithCampaignComponentsSanitized[];
  loading: boolean;
  error?: ApolloError;
  paginationProps: IPaginationProps;
  onEdit: (campaignId: string) => void;
  onCampaignClick: (campaignId: string) => void;
  showOffers?: boolean;
};

export const CampaignsTable = ({
  campaigns,
  loading,
  error,
  paginationProps,
  onEdit,
  onCampaignClick,
  showOffers,
}: CampaignsTableProps) => {
  const isUserPermittedToEditCampaign = useIsUserPermittedTo(Feature.CAMPAIGN_EDIT);
  const isUserPermittedToEditCampaignComponent = useIsUserPermittedTo(
    Feature.CAMPAIGN_COMPONENT_EDIT,
  );
  const isUserPermittedToDeleteCampaignComponent = useIsUserPermittedTo(
    Feature.CAMPAIGN_COMPONENT_DELETE,
  );

  const areActionsVisible =
    isUserPermittedToEditCampaign ||
    isUserPermittedToEditCampaignComponent ||
    isUserPermittedToDeleteCampaignComponent;

  const renderData = (items: CampaignWithCampaignComponentsSanitized[]) => {
    const rows = items.map((item) => (
      <CampaignTableRow
        key={item.uuid}
        campaign={item}
        onItemClick={onCampaignClick}
        onEdit={onEdit}
        showOffers={showOffers}
        isUserPermittedToEditCampaign={isUserPermittedToEditCampaign}
        isUserPermittedToEditCampaignComponent={isUserPermittedToEditCampaignComponent}
        isUserPermittedToDeleteCampaignComponent={isUserPermittedToDeleteCampaignComponent}
      />
    ));

    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>External Id</TableCell>
              {areActionsVisible && <TableCell align='right'>Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>{rows}</TableBody>
        </Table>
        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<CampaignWithCampaignComponentsSanitized>
      error={error}
      loading={loading}
      items={campaigns}
      renderData={renderData}
    />
  );
};
